<template>
  <b-modal :active.sync="isModalActive" :width="720" scroll="keep" @close="onModalClose">
    <div class="weekly-register-progress-modal">
      <header class="weekly-register-progress-modal__header">
        <h2 class="weekly-register-progress-modal__title">
          {{ $t('register_progress').toUpperCase() }}
        </h2>
        <p v-if="assignmentLabel" class="weekly-register-progress-modal__subtitle">
          {{ $t('assignment') }}: {{ assignmentLabel }}
        </p>
      </header>

      <div v-if="isLoading" class="weekly-register-progress-modal__loading">
        <b-loading :active="true" :is-full-page="false" />
      </div>

      <template v-else>
        <div class="weekly-register-progress-modal__summary">
          <span>
            {{ $t('scheduled') }}:
            <strong>{{ formatQuantity(scheduledQuantity) }} {{ unitLabel }}</strong>
          </span>
          <span>
            {{ $t('already_executed_quantity') }}:
            <strong>{{ formatQuantity(executedQuantity) }} {{ unitLabel }}</strong>
          </span>
        </div>

        <b-field :label="$t('executed_quantity')" class="weekly-register-progress-modal__field">
          <div class="weekly-register-progress-modal__quantity-row">
            <input
              v-model.number="form.executed_quantity"
              type="number"
              min="0"
              :max="pendingQuantity > 0 ? pendingQuantity : undefined"
              step="0.01"
              class="weekly-register-progress-modal__quantity-input"
              :disabled="assignmentCompleted"
            />
            <span class="weekly-register-progress-modal__unit">{{ unitLabel }}</span>
            <button
              type="button"
              class="weekly-register-progress-modal__complete-btn"
              :disabled="assignmentCompleted || pendingQuantity <= 0"
              @click="setCompletedQuantity"
            >
              {{ $t('completed').toUpperCase() }}
            </button>
          </div>
        </b-field>

        <b-field :label="$t('registration_period')" class="weekly-register-progress-modal__field">
          <div class="weekly-register-progress-modal__date-grid">
            <b-field :label="$t('date_start')">
              <b-datepicker
                v-model="formStartDate"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :min-date="activityDateMin"
                :max-date="formEndDate || activityDateMax"
                :disabled="assignmentCompleted"
              />
            </b-field>
            <b-field :label="$t('date_end')">
              <b-datepicker
                v-model="formEndDate"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :min-date="formStartDate || activityDateMin"
                :max-date="activityDateMax"
                :disabled="assignmentCompleted"
              />
            </b-field>
          </div>
          <p v-if="activityDateRangeLabel" class="weekly-register-progress-modal__hint">
            {{ $t('weekly_register_within_subactivity_range', { range: activityDateRangeLabel }) }}
          </p>
        </b-field>

        <div class="weekly-register-progress-modal__resource-grid">
          <b-field :label="$t('assigned_people_count')">
            <div class="weekly-register-progress-modal__resource-input">
              <b-icon pack="fas" icon="users" size="is-small" class="weekly-register-progress-modal__resource-icon" />
              <input
                :value="form.people"
                type="number"
                min="1"
                :max="maxPeopleLimit != null ? maxPeopleLimit : undefined"
                step="1"
                required
                class="weekly-register-progress-modal__number-input"
                @input="onPeopleInput"
              />
              <span class="weekly-register-progress-modal__resource-suffix">pers.</span>
            </div>
            <p v-if="maxPeopleLimit != null" class="weekly-register-progress-modal__hint">
              {{ $t('weekly_people_subactivity_limit', { max: maxPeopleLimit }) }}
            </p>
          </b-field>

          <b-field :label="$t('dedicated_man_hours')">
            <div class="weekly-register-progress-modal__resource-input">
              <b-icon pack="fas" icon="clock" size="is-small" class="weekly-register-progress-modal__resource-icon" />
              <input
                :value="dedicatedHoursDisplay"
                type="number"
                readonly
                tabindex="-1"
                class="weekly-register-progress-modal__number-input weekly-register-progress-modal__number-input--readonly"
              />
              <span class="weekly-register-progress-modal__resource-suffix">h</span>
            </div>
          </b-field>
        </div>

        <p class="weekly-register-progress-modal__hint weekly-register-progress-modal__hint--spaced">
          {{ $t('weekly_man_hours_auto_hint', { man_hour: projectManHour }) }}
        </p>

        <div class="weekly-register-progress-modal__metrics">
          <div class="weekly-register-progress-modal__metric">
            <span class="weekly-register-progress-modal__metric-label">{{ $t('yield') }}</span>
            <span class="weekly-register-progress-modal__metric-value">{{ displayYield }}</span>
          </div>
          <div class="weekly-register-progress-modal__metric">
            <span class="weekly-register-progress-modal__metric-label">{{ $t('hh_per_person') }}</span>
            <span class="weekly-register-progress-modal__metric-value">{{ displayHhPerPerson }}</span>
          </div>
          <div class="weekly-register-progress-modal__metric">
            <span class="weekly-register-progress-modal__metric-label">{{ $t('incurred_cost') }}</span>
            <span class="weekly-register-progress-modal__metric-value">{{ displayIncurredCost }}</span>
          </div>
        </div>
      </template>

      <footer class="weekly-register-progress-modal__footer">
        <button
          type="button"
          class="weekly-register-progress-modal__cancel-btn"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="weekly-register-progress-modal__save-btn"
          :disabled="isSaving || isLoading || assignmentCompleted"
          @click="submit"
        >
          {{ $t('save_progress') }}
        </button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { quantityDisplayDecimals } from '·/helpers/quantity-display'
import EventBus from '~/plugins/bus'

const DEFAULT_WORK_DAYS = {
  sunday: false,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
}

function weekDaysObjectToArray(workDays) {
  const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const source = workDays && typeof workDays === 'object' ? workDays : DEFAULT_WORK_DAYS

  return order.reduce((acc, name, index) => {
    if (source[name]) {
      acc.push(index)
    }
    return acc
  }, [])
}

function countWorkingDaysInRange(startDateStr, endDateStr, workingDayIndexes) {
  const start = moment(startDateStr, 'YYYY-MM-DD', true)
  const end = moment(endDateStr, 'YYYY-MM-DD', true)

  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return 0
  }

  let count = 0
  const cursor = start.clone()

  while (cursor.isSameOrBefore(end, 'day')) {
    if (workingDayIndexes.includes(cursor.day())) {
      count += 1
    }
    cursor.add(1, 'day')
  }

  return count
}

function parseApiDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.toDate() : null
}

function formatApiDate(value) {
  if (!value) {
    return null
  }
  return moment(value).format('YYYY-MM-DD')
}

export default {
  name: 'weekly-register-progress-modal',

  emits: ['saved'],

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      isSaving: false,
      activityId: null,
      assignmentId: null,
      assignmentRow: null,
      assignmentData: null,
      activityStartDate: null,
      activityEndDate: null,
      activityPeopleLimit: null,
      form: {
        executed_quantity: null,
        start_date: null,
        end_date: null,
        people: null,
        observation: '',
      },
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    assignmentCompleted() {
      return !!(this.assignmentRow && this.assignmentRow.completed)
    },

    assignmentLabel() {
      if (!this.assignmentRow) {
        return ''
      }
      const code = this.assignmentRow.code || ''
      const name = this.assignmentRow.name || this.assignmentRow.description || ''
      if (code && name) {
        return `${code} · ${name}`
      }
      return code || name
    },

    unitLabel() {
      if (this.assignmentData && this.assignmentData.unit) {
        return this.assignmentData.unit.code || this.assignmentData.unit.name || ''
      }
      return this.assignmentRow && (this.assignmentRow.unit_name || this.assignmentRow.unit)
        ? (this.assignmentRow.unit_name || this.assignmentRow.unit)
        : ''
    },

    scheduledQuantity() {
      if (this.assignmentData && this.assignmentData.total_quantity != null) {
        return parseFloat(this.assignmentData.total_quantity)
      }
      if (this.assignmentRow) {
        const total = this.assignmentRow.total != null
          ? this.assignmentRow.total
          : this.assignmentRow.scheduled_quantity
        return parseFloat(total)
      }
      return 0
    },

    executedQuantity() {
      const value = this.assignmentData
        ? this.assignmentData.executed_quantity
        : (this.assignmentRow && this.assignmentRow.executed_quantity)
      const num = parseFloat(value)
      return Number.isNaN(num) ? 0 : num
    },

    pendingQuantity() {
      const pending = this.scheduledQuantity - this.executedQuantity
      return pending > 0 ? pending : 0
    },

    formStartDate: {
      get() {
        return parseApiDate(this.form.start_date)
      },
      set(value) {
        this.form.start_date = formatApiDate(value)
      },
    },

    formEndDate: {
      get() {
        return parseApiDate(this.form.end_date)
      },
      set(value) {
        this.form.end_date = formatApiDate(value)
      },
    },

    activityDateMin() {
      return parseApiDate(this.activityStartDate)
    },

    activityDateMax() {
      return parseApiDate(this.activityEndDate)
    },

    activityDateRangeLabel() {
      if (!this.activityDateMin || !this.activityDateMax) {
        return ''
      }
      const format = 'DD/MM/YYYY'
      return `${moment(this.activityDateMin).format(format)} – ${moment(this.activityDateMax).format(format)}`
    },

    projectManHour() {
      const configs = this.project && this.project.configs
      const raw = configs && configs.man_hour != null && configs.man_hour !== ''
        ? Number(configs.man_hour)
        : null

      if (raw != null && !Number.isNaN(raw) && raw > 0) {
        return raw
      }
      return 8
    },

    projectWorkingDayIndexes() {
      const configs = this.project && this.project.configs
      return weekDaysObjectToArray(configs && configs.work_days)
    },

    workingDaysInPeriod() {
      return countWorkingDaysInRange(
        this.form.start_date,
        this.form.end_date,
        this.projectWorkingDayIndexes
      )
    },

    calculatedDedicatedHours() {
      const people = parseInt(this.form.people, 10)
      if (Number.isNaN(people) || people <= 0) {
        return 0
      }

      const days = this.workingDaysInPeriod
      if (days <= 0) {
        return 0
      }

      return Number((people * this.projectManHour * days).toFixed(5))
    },

    dedicatedHoursDisplay() {
      const hours = Number(this.calculatedDedicatedHours)
      if (!Number.isFinite(hours)) {
        return ''
      }
      return String(Math.round(hours * 100) / 100)
    },

    maxPeopleLimit() {
      return this.parsePeopleLimit(this.activityPeopleLimit)
    },

    displayYield() {
      const qty = parseFloat(this.form.executed_quantity)
      const hours = this.calculatedDedicatedHours
      if (Number.isNaN(qty) || Number.isNaN(hours) || hours <= 0) {
        return '—'
      }
      return `${(qty / hours).toFixed(3)} ${this.unitLabel}/h`
    },

    displayHhPerPerson() {
      return `${this.projectManHour} h`
    },

    assignmentUnitPrice() {
      const fromData = this.assignmentData && this.assignmentData.unit_price
      const fromRow = this.assignmentRow && this.assignmentRow.unit_price
      const raw = fromData != null && fromData !== '' ? fromData : fromRow
      const num = parseFloat(raw)
      return Number.isNaN(num) ? null : num
    },

    displayIncurredCost() {
      const unitPrice = this.assignmentUnitPrice
      const qty = parseFloat(this.form.executed_quantity)
      if (unitPrice == null || Number.isNaN(qty) || qty <= 0) {
        return '—'
      }

      const cost = unitPrice * qty
      const currency = '€'
      return `${cost.toFixed(2).replace('.', ',')} ${currency}`
    },
  },

  methods: {
    async show({
      assignment = null,
      activityId = null,
      activityStartDate = null,
      activityEndDate = null,
      activityPeople = null,
    } = {}) {
      this.assignmentRow = assignment
      this.assignmentId = assignment && assignment.id != null ? assignment.id : null
      this.activityId = activityId
      this.activityStartDate = activityStartDate
      this.activityEndDate = activityEndDate
      this.activityPeopleLimit = this.parsePeopleLimit(activityPeople)
      this.resetForm()
      this.isModalActive = true
      await this.loadModalContext()
    },

    parsePeopleLimit(value) {
      if (value == null || value === '') {
        return null
      }
      const num = parseInt(value, 10)
      if (Number.isNaN(num) || num <= 0) {
        return null
      }
      return num
    },

    async loadModalContext() {
      this.isLoading = true
      try {
        const requests = []

        if (this.assignmentId) {
          requests.push(axios.get(`/api/v2/assignment/${this.assignmentId}`))
        } else {
          requests.push(Promise.resolve(null))
        }

        if (this.activityId && this.activityPeopleLimit == null) {
          requests.push(axios.get(`/api/v2/activity/${this.activityId}`))
        } else {
          requests.push(Promise.resolve(null))
        }

        const [assignmentRes, activityRes] = await Promise.all(requests)

        if (assignmentRes && assignmentRes.data && assignmentRes.data.success && assignmentRes.data.assignment) {
          this.assignmentData = assignmentRes.data.assignment
        }

        if (activityRes && activityRes.data && activityRes.data.success && activityRes.data.data) {
          const people = this.parsePeopleLimit(activityRes.data.data.people)
          if (people != null) {
            this.activityPeopleLimit = people
          }
        }
      } catch (error) {
        console.error('[weekly-register-progress-modal] load context error:', error)
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      const today = moment().format('YYYY-MM-DD')
      this.assignmentData = null
      this.form = {
        executed_quantity: null,
        start_date: today,
        end_date: today,
        people: null,
        observation: '',
      }
    },

    setCompletedQuantity() {
      if (this.pendingQuantity > 0) {
        this.form.executed_quantity = Number(this.pendingQuantity.toFixed(5))
      }
    },

    onPeopleInput(event) {
      const raw = event && event.target ? event.target.value : ''
      if (raw === '') {
        this.form.people = null
        return
      }

      let value = parseInt(raw, 10)
      if (Number.isNaN(value) || value < 1) {
        value = 1
      }

      if (this.maxPeopleLimit != null && value > this.maxPeopleLimit) {
        value = this.maxPeopleLimit
      }

      this.form.people = value
      if (event && event.target) {
        event.target.value = value
      }
    },

    /**
     * Payload alineado con assignment-contribute.vue (Resource, siempre progreso parcial).
     * POST /api/v2/assignments/activity/{activityId}/{assignmentId}
     */
    buildApiPayload() {
      const enteredQty = parseFloat(this.form.executed_quantity)
      const people = parseInt(this.form.people, 10)

      return {
        executed_quantity: Number.isNaN(enteredQty) ? 0 : enteredQty,
        start_date: this.form.start_date,
        end_date: this.form.end_date,
        observation: null,
        executed_units: Number.isNaN(enteredQty) ? null : enteredQty,
        executed_length: null,
        executed_width: null,
        executed_height: null,
        is_actual_progress: false,
        people: Number.isNaN(people) ? null : people,
      }
    },

    validateForm() {
      if (!this.activityId || !this.assignmentId) {
        this.$notify.error('assignment_update_error')
        return false
      }

      const qty = parseFloat(this.form.executed_quantity)
      if (Number.isNaN(qty) || qty <= 0) {
        this.$notify.error('please_complete_required_fields')
        return false
      }

      if (!this.form.start_date || !this.form.end_date) {
        this.$notify.error('please_complete_required_fields')
        return false
      }

      if (moment(this.form.end_date).isBefore(this.form.start_date, 'day')) {
        this.$notify.error('please_complete_required_fields')
        return false
      }

      const people = parseInt(this.form.people, 10)
      if (Number.isNaN(people) || people <= 0) {
        this.$notify.error('please_complete_required_fields')
        return false
      }

      if (this.maxPeopleLimit != null && people > this.maxPeopleLimit) {
        this.$notify.error('weekly_people_subactivity_limit', { max: this.maxPeopleLimit })
        return false
      }

      return true
    },

    async submit() {
      if (this.isSaving) {
        return
      }
      if (!this.validateForm()) {
        return
      }

      this.isSaving = true
      const url = `/api/v2/assignments/activity/${this.activityId}/${this.assignmentId}`

      try {
        const { data } = await axios.post(url, this.buildApiPayload())
        if (data && data.success) {
          this.$notify.success('assignment_update_success')
          this.notifyFrontendRefresh()
          this.$emit('saved', {
            activityId: this.activityId,
            assignmentId: this.assignmentId,
          })
          this.close()
          return
        }

        this.$notify.error(data && data.msg ? data.msg : 'assignment_update_error')
      } catch (error) {
        let message = this.$t('assignment_update_error')
        if (error.response && error.response.status === 422 && error.response.data.errors) {
          const errors = error.response.data.errors
          const firstKey = Object.keys(errors)[0]
          if (firstKey && errors[firstKey] && errors[firstKey][0]) {
            message = errors[firstKey][0]
          }
        }
        this.$notify.error(message)
        console.error('[weekly-register-progress-modal] submit error:', error)
      } finally {
        this.isSaving = false
      }
    },

    notifyFrontendRefresh() {
      EventBus.$emit('refreshAssignmentChangedNotifications', {
        activityId: this.activityId,
        assignmentId: this.assignmentId,
      })
      EventBus.$emit('refreshActivitiesNotifications', true)
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.assignmentRow = null
      this.assignmentId = null
      this.assignmentData = null
      this.activityPeopleLimit = null
      this.isSaving = false
      this.isLoading = false
    },

    formatQuantity(value) {
      const scale = quantityDisplayDecimals(this.project)
      const num = parseFloat(value)
      if (Number.isNaN(num)) {
        return (0).toFixed(scale)
      }
      return num.toFixed(scale)
    },
  },
}
</script>

<style scoped>
.weekly-register-progress-modal {
  padding: 1rem 1.25rem 1.25rem;
  background-color: #fff;
}

.weekly-register-progress-modal__header {
  margin-bottom: 1rem;
}

.weekly-register-progress-modal__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #3b66bc;
}

.weekly-register-progress-modal__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.8125rem;
  color: #4b5563;
  line-height: 1.4;
}

.weekly-register-progress-modal__loading {
  min-height: 8rem;
  position: relative;
}

.weekly-register-progress-modal__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  background-color: #eef5fc;
  border: 1px solid #c5daf0;
  font-size: 0.8125rem;
  color: #1f2937;
}

.weekly-register-progress-modal__field {
  margin-bottom: 1rem;
}

.weekly-register-progress-modal__quantity-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.weekly-register-progress-modal__quantity-input {
  width: 7rem;
  height: 2.5rem;
  padding: 0 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  font-weight: 600;
}

.weekly-register-progress-modal__unit {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.weekly-register-progress-modal__complete-btn {
  padding: 0.4rem 0.85rem;
  border: 2px solid #3b66bc;
  border-radius: 0.375rem;
  background: #fff;
  color: #3b66bc;
  font-size: 0.6875rem;
  font-weight: 700;
  cursor: pointer;
}

.weekly-register-progress-modal__complete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.weekly-register-progress-modal__date-grid,
.weekly-register-progress-modal__resource-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
}

.weekly-register-progress-modal__hint {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.weekly-register-progress-modal__hint--spaced {
  margin-bottom: 1rem;
}

.weekly-register-progress-modal__resource-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.weekly-register-progress-modal__resource-icon {
  color: #6b7280;
}

.weekly-register-progress-modal__number-input {
  flex: 1;
  min-width: 0;
  height: 2.25rem;
  padding: 0 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.weekly-register-progress-modal__number-input--readonly {
  background-color: #f3f4f6;
  color: #374151;
  cursor: default;
}

.weekly-register-progress-modal__resource-suffix {
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-register-progress-modal__metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}

.weekly-register-progress-modal__metric-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.weekly-register-progress-modal__metric-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
}

.weekly-register-progress-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.weekly-register-progress-modal__cancel-btn,
.weekly-register-progress-modal__save-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-register-progress-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
}

.weekly-register-progress-modal__save-btn {
  border: none;
  background: #3b66bc;
  color: #fff;
}

.weekly-register-progress-modal__save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
